import { ReactComponent as TrendDown } from "assets/TrendDown.svg";
import { ReactComponent as TrendUp } from "assets/TrendUp.svg";
import { ICardElementWithChildren } from "backend/model/model";
import { PerformanceDonut } from "design/components/charts/PerformanceDonut";
import { t } from "i18next";
import {
  IGaugeSettings,
  ITargetCardSettings,
} from "qpr-base/configuration/types";
import { makeThemedStyles } from "qpr-base/viewcomponents/utils";
import React from "react";
import CardWrapper from "./InitiativeDetailsCardWrapper";

/** Same numeric display as KPICard: 2 decimals, "-" when missing/invalid */
function formatKpiFieldValue(
  raw: ICardElementWithChildren[keyof ICardElementWithChildren]
): string {
  if (raw === undefined || raw === null || raw === "") {
    return "-";
  }
  const n = Number(raw);
  if (Number.isFinite(n)) {
    return n.toFixed(2);
  }
  if (typeof raw === "string") {
    return raw;
  }
  return "-";
}

const useStylesForPerf = makeThemedStyles(
  "KPIDetailsContent.Performance",
  (theme) => ({
    rootcontainer: {
      display: "flex",
      flexDirection: "column",
    },
    headerStyle: {
      fontSize: "12px",
      fontWeight: 500,
      lineHeight: "14.63px",
      margin: "14px auto",
      selfAlign: "center",
      fontFamily: "Montserrat",
      fontStyle: "normal",
      color: "#4C5157",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      padding: "4px auto",
      width: "auto",
      height: "23px",
      background: "#E1F5FF",
      borderRadius: "15px",
    },
    Year: {
      marginLeft: "10px",
    },
    YearText: {
      color: "rgba(76, 81, 87)",
    },
    ProsContainer: {
      marginLeft: "10px",
    },
    ValuesRow: {
      gap: "20px",
    },
    ValuesTxt: {
      marginLeft: "0px",
    },
    dataContainer: {
      width: "80%",
      flexDirection: "column",
      marginLeft: "0em",
      color: "#656A72",
      display: "flex",
      gap: "1.2em",
    },
    donutChartContainer: {
      width: "60%",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    },
    periodStyles: {
      color: "rgba(76, 81, 87, 1)",
      fontSize: "2em",
      lineHeight: "2.28em",
    },
    text: {
      fontSize: "24px",
      color: "rgba(17, 94, 103, 1)",
      lineHeight: "0.9em",
      marginLeft: "-75px",
      marginTop: "5px",
    },
    perfStyle: {
      display: "flex",
      justifyContent: "flex-start",
    },
    valueContainer: {
      display: "flex",
      fontSize: "13px",
      lineHeight: "1.5em",
      flexWrap: "wrap",
    },
    datatext: {
      width: "80px",
      fontWeight: 500,
      marginRight: "0.8em",
    },
    datavalue: {
      fontWeight: 600,
      display: "flex",
      flexWrap: "nowrap",
    },
    trendUp: {
      color: "rgba(39, 182, 177,.7)",
      marginTop: "-1px",
      marginLeft: "10px",
      width: "20px",
      height: "20px",
    },
    trendDown: {
      color: "rgba(189, 41, 44,.8)",
      marginTop: "-1px",
      marginLeft: "10px",
      width: "20px",
      height: "20px",
    },
    title: {
      fontSize: "16px",
    },
  })
);

const donutTextStyle = ({ color }: { color?: string }) =>
  makeThemedStyles("KPIDetailsContent.DonutChart", (theme) => ({
    donutText: {
      fontSize: "48px",
      alignmentBaseline: "middle",
      strokeWidth: "0px",
      fill: color || "rgb(84, 84, 84, 100%)",
    },
  }));

const useStylesForDonutChart = makeThemedStyles(
  "KPIDetailsContent.DonutChart",
  (theme) => ({
    DonutChart: {
      width: "13.71em",
      height: "13.71em",
    },
    line: {
      stroke: "rgba(113, 206, 203)",
      strokeWidth: "20px",
      strokeLinecap: "round",
      fill: "none",
    },
    background: {
      fill: "rgb(250, 250, 250, 0.8)",
      strokeWidth: "20px",
      strokeLinecap: "round",
      stroke: "rgb(207, 239, 239)",
    },
    text: {
      fontSize: "30px",
      alignmentBaseline: "middle",
    },
    subtitle: {
      fontSize: "20px",
      color: "lightgray",
      alignmentBaseline: "middle",
    },
  })
);

const useTargetStyles = makeThemedStyles(
  "KPIDetailsContent.TargetCard",
  (theme) => ({
    valueContainer: {
      display: "flex",
      alignItems: "baseline",
      gap: "5px",
    },
    valueText: {
      fontSize: "3.88em",
    },
    unitText: {
      fontSize: "1.72em",
    },
  })
);

export const YearEndTargetCard = (props: {
  element: ICardElementWithChildren;
  settings: ITargetCardSettings;
}) => {
  const { element, settings } = props;
  const { title, value, unit } = settings;

  const styles = useTargetStyles();

  const val = value?.attribute
    ? element[value.attribute as keyof ICardElementWithChildren]
    : "-";

  const getValue = () => {
    if (value?.value) return value.value;
    if (value?.attribute)
      return element[value.attribute as keyof ICardElementWithChildren];
    return "-";
  };

  const getUnit = () => {
    if (unit?.value) return unit.value;
    if (unit?.attribute)
      return element[unit.attribute as keyof ICardElementWithChildren];
    return "-";
  };

  return (
    <CardWrapper
      title={t(title || "")}
      period={element.periodname || ""}
      color="b1b1b1"
    >
      <div className={styles.valueContainer}>
        <div className={styles.valueText}>{getValue()}</div>
        <div className={styles.unitText}>{getUnit()}</div>
      </div>
    </CardWrapper>
  );
};

export const KPIPerformanceCard = (
  props: React.PropsWithChildren<{
    period: string;
    element: ICardElementWithChildren;
    settings: IGaugeSettings;
    colorKey?: keyof ICardElementWithChildren;
  }>
) => {
  const { element, period, settings } = props;
  const { fields } = settings;
  const styles = useStylesForPerf();

  const getTrendIcon = (series: string, trend: string) => {
    if (series !== "ACT_prettyvalue") return null;
    return null;
  };

  return (
    <CardWrapper
      title={t('strategyReport.'+settings.title || "kpis.Target Achievement")}
      period={period}
      color={String(
        settings.colorAttribute
          ? element[
              settings.colorAttribute as keyof ICardElementWithChildren
            ] || "696969"
          : "696969"
      )}
      topSpace
    >
      <>
        <div className={styles.donutChartContainer}>
          {" "}
          <PerformanceDonut element={element} settings={settings} />
        </div>
        <div className={styles.dataContainer}>
          {(fields || []).map((field: any) => {
            const raw =
              element[field.attribute as keyof ICardElementWithChildren];
            const value = formatKpiFieldValue(raw);
            const trend = element["trend"] || "";
            return (
              <div className={styles.valueContainer} key={field.attribute}>
                <div className={styles.datatext}>
                  {t("configtypes."+field.title || field.attribute)}
                </div>
                <div className={styles.datavalue}>
                  {value}
                  <div
                    className={
                      trend === "-1" ? styles.trendDown : styles.trendUp
                    }
                  >
                    {value !== "-"
                      ? getTrendIcon(field.attribute, trend)
                      : null}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </>
    </CardWrapper>
  );
};
